import { clearAuthCookies } from '@artic/ui/lib/auth-cookies';
import { clearAppLocalStorage } from '@artic/ui/lib/supabase-cookie-storage';

/**
 * When the page loads with a Supabase implicit-grant callback in the URL
 * hash (e.g. fresh from a magic-link confirm), wipe any state left over
 * from a previous session in this browser before:
 *
 *   1. `ensureDualStorageSync` runs and potentially resurrects a stale
 *      `sb-*-auth-token` between localStorage and cookies,
 *   2. Recoil atoms (e.g. `partnerPicker`) subscribe and hydrate from
 *      stale `current_partner` localStorage,
 *   3. `partnerCookieEffect` writes a stale `artic_partner` cookie that
 *      the api-client's `customFetch` will then send as a `partner_name`
 *      header on the first authenticated request (`/current-user`),
 *      causing a backend rejection that bubbles up as a blank screen.
 *
 * Mirrors the cleanup that `LoginPage` performs on an unauthenticated
 * visit, but covers the happy-path magic-link arrival which bypasses
 * `/login` entirely.
 *
 * Returns true if a callback was detected and cleanup ran, false otherwise.
 */
export function clearPriorSessionOnAuthCallback(
  hash: string,
  cookieDomain: string | undefined,
): boolean {
  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  const expiresIn = Number(params.get('expires_in'));
  if (
    !params.get('access_token') ||
    !params.get('refresh_token') ||
    params.get('token_type') !== 'bearer' ||
    !Number.isFinite(expiresIn) ||
    expiresIn <= 0
  ) {
    return false;
  }
  clearAppLocalStorage();
  clearAuthCookies({ domain: cookieDomain });
  return true;
}
